import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Star } from 'lucide-react';

const HERO_BACKGROUNDS = [
  '/api/uploads/hero_owners_1.webp',
  '/api/uploads/hero_owners_2.png',
];
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png";

export default function Hero() {
  const canvasRef = useRef(null);
  const [bgIndex, setBgIndex] = useState(0);

  // Crossfade backgrounds every 6s
  useEffect(() => {
    const t = setInterval(() => {
      setBgIndex(i => (i + 1) % HERO_BACKGROUNDS.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  // subtle water ripple on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frame = 0;
    let animId;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      frame++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const t = frame * 0.008;
      for (let i = 0; i < 5; i++) {
        const x = canvas.width  * (0.2 + i * 0.16);
        const y = canvas.height * (0.5 + Math.sin(t + i * 1.2) * 0.08);
        const r = 60 + i * 30 + Math.sin(t + i) * 20;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0,   `rgba(79, 195, 212, ${0.06 + Math.sin(t + i) * 0.02})`);
        grad.addColorStop(0.5, `rgba(79, 195, 212, 0.02)`);
        grad.addColorStop(1,   'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const scrollToPrenota = () => {
    document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background carousel with crossfade + Ken-Burns zoom */}
      {HERO_BACKGROUNDS.map((src, i) => (
        <div
          key={src}
          data-testid={`hero-bg-${i}`}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${src})`,
            opacity: bgIndex === i ? 1 : 0,
            transition: 'opacity 1.6s ease-in-out',
            animation: bgIndex === i ? 'heroKenBurns 12s ease-in-out forwards' : 'none',
          }}
        />
      ))}
      {/* Dark overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Water ripple canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ mixBlendMode: 'screen', opacity: 0.8 }}
      />

      {/* Decorative neon lines */}
      <div className="absolute top-0 left-0 right-0 neon-line" />
      <div className="absolute bottom-0 left-0 right-0 neon-line" />

      {/* Content — pt-24 evita sovrapposizione con la navbar fissa */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto pt-24 pb-20">
        {/* Logo */}
        <div
          className="mb-8 flex justify-center animate-fade-in-up"
          style={{ animationDelay: '0.1s', animationFillMode: 'both' }}
          data-testid="hero-logo"
        >
          <img
            src={LOGO_URL}
            alt="La Peschiera Club"
            className="h-36 md:h-52 lg:h-64 object-contain drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 0 30px rgba(201,168,76,0.4))' }}
          />
        </div>

        {/* Subtitle */}
        <p
          className="section-eyebrow mb-4 animate-fade-in-up"
          style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
        >
          Valdengo (BI) &bull; Musica da Ballo
        </p>

        {/* Tagline */}
        <h1
          className="font-playfair text-4xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight animate-fade-in-up"
          style={{ animationDelay: '0.5s', animationFillMode: 'both' }}
          data-testid="hero-title"
        >
          <span
            className="hero-slogan block"
            style={{
              background: 'linear-gradient(90deg, #c9a84c 25%, #fff8e1 50%, #c9a84c 75%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shimmer 3s linear infinite',
              transition: 'all 0.4s ease',
              cursor: 'default',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.WebkitTextFillColor = 'white';
              e.currentTarget.style.backgroundImage = 'none';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.WebkitTextFillColor = 'transparent';
              e.currentTarget.style.backgroundImage = 'linear-gradient(90deg, #c9a84c 25%, #fff8e1 50%, #c9a84c 75%)';
            }}
          >
            Sala da Ballo
          </span>
          <span
            className="block italic"
            style={{
              background: 'linear-gradient(90deg, #c9a84c 25%, #fff8e1 50%, #c9a84c 75%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shimmer 3s linear infinite',
              animationDelay: '0.3s',
              transition: 'all 0.4s ease',
              cursor: 'default',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.WebkitTextFillColor = 'white';
              e.currentTarget.style.backgroundImage = 'none';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.WebkitTextFillColor = 'transparent';
              e.currentTarget.style.backgroundImage = 'linear-gradient(90deg, #c9a84c 25%, #fff8e1 50%, #c9a84c 75%)';
            }}
          >
            Eventi &amp; Concerti
          </span>
          <span
            className="block text-3xl md:text-4xl lg:text-5xl mt-2 tracking-widest"
            style={{
              background: 'linear-gradient(90deg, #c9a84c 25%, #fff8e1 50%, #c9a84c 75%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shimmer 3s linear infinite',
              animationDelay: '0.6s',
              transition: 'all 0.4s ease',
              cursor: 'default',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.WebkitTextFillColor = 'white';
              e.currentTarget.style.backgroundImage = 'none';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.WebkitTextFillColor = 'transparent';
              e.currentTarget.style.backgroundImage = 'linear-gradient(90deg, #c9a84c 25%, #fff8e1 50%, #c9a84c 75%)';
            }}
          >
            Dal 1962
          </span>
        </h1>

        {/* Stars */}
        <div
          className="flex justify-center gap-1 mb-4 animate-fade-in-up"
          style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
        >
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={18} className="text-gold fill-current" />
          ))}
        </div>

        {/* Social icons */}
        <div
          className="flex justify-center gap-5 mb-8 animate-fade-in-up"
          style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
        >
          {[
            {
              href: 'https://www.facebook.com/lapeschieradisco.it/?locale=it_IT',
              label: 'Facebook',
              brand: '#1877F2',
              shadow: 'rgba(24,119,242,0.55)',
              icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
            },
            {
              href: 'https://www.instagram.com/lapeschieradisco.it/',
              label: 'Instagram',
              brand: 'linear-gradient(135deg, #feda75 0%, #fa7e1e 25%, #d62976 55%, #962fbf 80%, #4f5bd5 100%)',
              shadow: 'rgba(214,41,118,0.55)',
              icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
            },
            {
              href: 'https://www.youtube.com/@RADIOLISCIO',
              label: 'YouTube',
              brand: '#FF0000',
              shadow: 'rgba(255,0,0,0.55)',
              icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
            },
          ].map(s => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              data-testid={`hero-social-${s.label.toLowerCase()}`}
              className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300"
              style={{
                background: s.brand,
                color: '#fff',
                boxShadow: `0 6px 22px ${s.shadow}, 0 0 0 1px rgba(255,255,255,0.08) inset`,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-3px) scale(1.1)';
                e.currentTarget.style.boxShadow = `0 12px 32px ${s.shadow}, 0 0 0 1px rgba(255,255,255,0.15) inset`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = `0 6px 22px ${s.shadow}, 0 0 0 1px rgba(255,255,255,0.08) inset`;
              }}
            >{s.icon}</a>
          ))}
        </div>

        {/* CTA */}
        <div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up"
          style={{ animationDelay: '0.9s', animationFillMode: 'both', paddingBottom: '3rem' }}
        >
          <button
            data-testid="hero-cta-btn"
            onClick={scrollToPrenota}
            className="btn-primary text-xl px-12 py-5"
            style={{ minHeight: '64px', fontSize: '1.2rem' }}
          >
            Prenota ora
          </button>
          <button
            data-testid="hero-events-btn"
            onClick={() => document.getElementById('eventi')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-secondary text-xl px-10 py-5"
            style={{ minHeight: '64px', fontSize: '1.2rem' }}
          >
            Prossimi eventi
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-gold transition-colors animate-bounce"
        data-testid="scroll-indicator"
      >
        <ChevronDown size={36} />
      </button>
    </section>
  );
}
